import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Restores the session on load — but only from a token this app is holding.
   *
   * The no-token early return is the whole point. This used to call `/auth/me`
   * regardless, with `credentials: 'include'` and no Authorization header, so
   * after a logout the only thing left to authenticate with was the cookie the
   * API sets alongside the token (it runs with
   * `Access-Control-Allow-Credentials: true`). The server answered 200, the app
   * believed it, and a refresh silently put the user back in the account they had
   * just signed out of.
   *
   * So: no token, no session. The credential this app stores is the one that
   * decides, and asking the server "am I still someone?" with ambient cookies is
   * exactly the question that resurrects a discarded login.
   */
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // Rejected token (expired, revoked, tampered): drop the identity too.
        // Clearing only the token left a stale user on screen with nothing
        // behind it — every request that user made would 401.
        localStorage.removeItem('token');
        setUser(null);
      }
    } catch (error) {
      // Network failure is not proof of anything, so the existing session is
      // left alone rather than logging someone out because their wifi dropped.
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /**
   * Signing out in one tab signs out the rest.
   *
   * Without this, a second tab keeps rendering the account — and its "Logout"
   * calls the API with a token that is already gone. Browsers fire `storage`
   * only in *other* tabs, so this cannot loop with the synthetic event logout
   * dispatches for the cart contexts; that one carries no `key`, hence the
   * re-read rather than trusting `e.newValue`.
   */
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key && event.key !== 'token') return;
      if (!localStorage.getItem('token')) setUser(null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = async (email, password) => {
    const response = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    const token = data.token || data.access_token;
    if (token) {
      localStorage.setItem('token', token);
    }
    setUser(data.user || data);
    return data;
  };

  const register = async (name, email, password) => {
    const response = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await response.json();
    return data;
  };

  const loginWithGoogle = async (credential) => {
    const response = await fetch(`${API}/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ credential })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Google login failed');
    }

    const data = await response.json();
    const token = data.token || data.access_token;
    if (token) {
      localStorage.setItem('token', token);
    }
    setUser(data.user || data);
    return data;
  };

  /**
   * Signs out locally first, then tells the server.
   *
   * The order matters. This used to `await` the logout request before clearing
   * anything, so a slow or failed call left the app fully signed in — on a dead
   * network the button did nothing at all. Nothing the server says can make a
   * logout fail from the user's side: the local session goes either way, and the
   * request is best-effort on top of that.
   */
  const logout = async () => {
    const token = localStorage.getItem('token');

    localStorage.removeItem('token');
    // Clear carts on logout so the next user or guest session starts fresh
    localStorage.removeItem('guest_cart');
    localStorage.removeItem('gift_card_cart');

    // Dispatch a custom event to notify other contexts (like GiftCardCartContext)
    // that the storage has been cleared so they can reset their state.
    window.dispatchEvent(new Event('storage'));

    setUser(null);

    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        // Sent with the token that is being retired: the server cannot revoke a
        // session it can't identify, and it has already been dropped locally.
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const changePassword = async (current_password, new_password) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      credentials: 'include',
      body: JSON.stringify({ current_password, new_password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to change password');
    }

    return await response.json();
  };

  /**
   * Starts a password reset. Unauthenticated by design — the caller is locked out.
   *
   * The response is deliberately the same whether or not the email is registered
   * ("If an account exists for that email…"), so this must never be turned into a
   * "no such account" message: that would make the endpoint an account-enumeration
   * oracle, which is exactly what the server is avoiding.
   */
  const forgotPassword = async (email) => {
    const response = await fetch(`${API}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.detail || 'Could not send the reset link');
    return data;
  };

  /**
   * Completes a reset with the token from the emailed link.
   *
   * 400 means the link is expired, already used or malformed — the pages treat
   * that as its own state rather than a toast, because the only way forward is
   * to request a fresh link.
   */
  const resetPassword = async (token, new_password) => {
    const response = await fetch(`${API}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.detail || 'Could not reset the password');
      error.status = response.status;
      throw error;
    }
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, googleLogin: loginWithGoogle, register, logout, checkAuth, changePassword, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
